import { z } from 'zod';
/*
 * La politique d'envoi des médias de terrain, **partagée** : l'API l'applique (`express.raw`,
 * `apps/api/src/sync/media-policy.ts`), et la tablette la lit au moment où l'agent choisit un
 * fichier — un envoi qu'elle sait refusé d'avance ne part pas, parce qu'un refus à l'envoi rend
 * le relevé entier intransmissible. Deux copies de ces valeurs divergeraient en silence.
 */
/**
 * Ce que l'API accepte comme média de terrain, **et rien d'autre** — tout ce que la fenêtre d'ajout
 * de la tablette propose : photo, document, vidéo.
 *
 * - images : `image/jpeg` pour les photos, `image/png` pour les signatures et le schéma,
 *   `image/webp` pour les navigateurs qui recompressent, `image/heic`/`heif` pour une photo prise
 *   dans la galerie d'un iPad, `image/gif` ;
 * - documents : `application/pdf` — un plan, un courrier ;
 * - vidéos : `video/mp4` (Android), `video/quicktime` (iPad), `video/webm`, `video/3gpp`.
 *
 * ⚠️ **Une pièce de terrain ne se refuse pas pour son format** (M21) : les médias montent avant le
 * dépôt, et un `415` rendait le relevé entier intransmissible, sans issue — ni reprise de la
 * fiche, ni retrait de la pièce. Seul le rapport choisit ce qu'il imprime (`readableImage`).
 *
 * ⚠️ Ce n'est **pas** une validation de contenu : le type déclaré n'est pas vérifié contre les
 * octets. La liste sert à borner ce qui traverse l'API, pas à garantir ce que c'est.
 */
export const ACCEPTED_MEDIA_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
    'image/gif',
    'application/pdf',
    'video/mp4',
    'video/quicktime',
    'video/webm',
    'video/3gpp',
];
/**
 * Plafond d'un envoi, en octets, tel qu'`express.raw` l'applique.
 *
 * 25 Mo tient une photo de tablette moderne sans recompression, et refuse un envoi qui ne peut
 * être qu'une erreur. ⚠️ **Pour une vidéo, c'est court** : à ~16 Mbit/s (1080p d'une tablette),
 * 25 Mo font une douzaine de secondes, quatre ou cinq en 4K — le passage caméra d'un regard, pas
 * celui d'un réseau. ⚠️ Le dépassement rend `413` **avant** le contrôleur : ce n'est pas un
 * refus métier qu'on pourrait assouplir, c'est le corps qui n'est jamais lu.
 */
export const MEDIA_UPLOAD_LIMIT_BYTES = 25 * 1024 * 1024;
/**
 * Plafond d'une vidéo envoyée **directement au stockage**, par URL pré-signée (#418) : 1 Go. Une
 * vidéo de la caméra d'inspection pèse 250 à 385 Mo, et elle se garde telle quelle, sans
 * compression : c'est une preuve en cas de contestation.
 *
 * ⚠️ Ne vaut que si le stockage signe les envois. En mode disque (`MEDIA_STORAGE_DIR`), le relais
 * de 25 Mo reste le chemin : la tablette l'apprend par `GET /sync/media-policy`.
 */
export const VIDEO_UPLOAD_LIMIT_BYTES = 1024 * 1024 * 1024;
/** Le type déclaré est-il une vidéo ? */
export function isVideoMediaType(type) {
    return type.toLowerCase().startsWith('video/');
}
/**
 * Ce qu'un fichier peut peser, selon son type et la limite vidéo que le serveur annonce : 25 Mo
 * pour tout ce qui passe par le relais, la limite annoncée pour une vidéo.
 */
export function uploadLimitOf(type, videoLimitBytes) {
    return isVideoMediaType(type) ? videoLimitBytes : MEDIA_UPLOAD_LIMIT_BYTES;
}
/** Ce que l'API annonce à la tablette : la limite d'une vidéo, 1 Go en direct, 25 Mo en relais. */
export const mediaPolicySchema = z.object({ videoLimitBytes: z.number().int().positive() });
/** La demande d'une URL d'envoi : une vidéo, et sa taille, signée avec l'URL. */
export const mediaUploadRequestSchema = z.object({
    contentType: z
        .enum(ACCEPTED_MEDIA_TYPES)
        .refine(isVideoMediaType, 'seule une vidéo monte en direct'),
    size: z.number().int().positive().max(VIDEO_UPLOAD_LIMIT_BYTES),
});
/**
 * La réponse : une URL d'envoi signée pour peu de temps, ou `relay` quand le stockage n'en signe
 * pas (mode disque) — la tablette passe alors par `PUT /sync/media/:id`.
 */
export const mediaUploadTicketSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('direct'),
        url: z.url(),
        expiresAt: z.iso.datetime({ offset: true }),
    }),
    z.object({ mode: z.literal('relay') }),
]);
/**
 * Le nombre de pièces jointes qu'un contrôle porte au plus — photos, documents et vidéos, sans la
 * signature ni l'image du schéma, que la tablette produit.
 *
 * ⚠️ **Un blocage, exception nommée à M21** : demandé par GHP (réunion du 22/09), retenu tel quel
 * le 06/10. La tablette ferme l'ajout à la dixième pièce ; l'API n'en refuse pas une onzième — un
 * refus au dépôt ferait tomber l'enveloppe entière. `MAX_MEDIA_PER_INSPECTION` reste le plafond
 * de l'enveloppe, médias produits compris.
 */
export const MAX_ATTACHMENTS_PER_INSPECTION = 10;
/** Le type déclaré d'un fichier est-il de ceux que l'API range ? */
export function isAcceptedMediaType(type) {
    return ACCEPTED_MEDIA_TYPES.includes(type.toLowerCase());
}
/**
 * L'URL de lecture signée d'une pièce, pour le bureau (#418) — `null` quand le stockage n'en
 * signe pas (mode disque) : les octets se relisent alors par l'API.
 */
export const mediumLinkSchema = z.object({ url: z.url().nullable() });
//# sourceMappingURL=media-policy.js.map